import { ChipGroup } from '../pages/spending/controls';
import TopBar from './TopBar';
import styles from './FilterPanel.module.css';

// The Filter & sort screen shared by View Payments and View Expenses, and the lines a
// filtered list shows under its top bar. Choices live in the host screen, so they survive
// a trip to a payment and back; this file only draws them. Every choice is a chip (.claude/rules/ux.md:
// no dropdowns).

export interface Nouns {
  one: string; // "payment"
  other: string; // "payments"
}

function counted(n: number, nouns: Nouns): string {
  return `${n} ${n === 1 ? nouns.one : nouns.other}`;
}

export interface ChipSection {
  heading: string; // also the group's accessible name
  options: { value: string; label: string }[];
  selected: string;
  onSelect: (value: string) => void;
}

interface FilterPanelProps {
  sections: ChipSection[];
  shownCount: number;
  nouns: Nouns;
  onClearAll: () => void;
  onClose: () => void;
}

// A full screen in place of the list. Choices apply as they are tapped; "Done" and
// "Show N …" both close it.
export default function FilterPanel({ sections, shownCount, nouns, onClearAll, onClose }: FilterPanelProps) {
  return (
    <main className={styles.container}>
      <TopBar title="Filter & sort" backLabel="Done" onBack={onClose} />
      {sections.map((s) => (
        <section key={s.heading} className={styles.section}>
          <h2 className={styles.sectionHeading}>{s.heading}</h2>
          <ChipGroup options={s.options} selected={s.selected} onSelect={s.onSelect} ariaLabel={s.heading} />
        </section>
      ))}
      <button type="button" className={styles.secondaryButton} onClick={onClearAll}>
        Clear all
      </button>
      <button type="button" className={styles.primaryButton} onClick={onClose}>
        {`Show ${counted(shownCount, nouns)}`}
      </button>
    </main>
  );
}

interface FilterStatusProps {
  shown: number;
  total: number;
  nouns: Nouns;
  activeParts: string[];
  onClearAll: () => void;
}

// "Showing N of M …", and — only when a choice differs from the defaults — one line naming
// the choices, with "Clear all".
export function FilterStatus({ shown, total, nouns, activeParts, onClearAll }: FilterStatusProps) {
  return (
    <div className={styles.status}>
      <p className={styles.statusLine}>{`Showing ${shown} of ${counted(total, nouns)}`}</p>
      {activeParts.length > 0 && (
        <>
          <p className={styles.activeLine}>{`Chosen: ${activeParts.join(' · ')}`}</p>
          <button type="button" className={styles.secondaryButton} onClick={onClearAll}>
            Clear all
          </button>
        </>
      )}
    </div>
  );
}

// No row matches the current choices.
export function NoMatch({ nouns, onClearAll }: { nouns: Nouns; onClearAll: () => void }) {
  return (
    <div className={styles.status}>
      <p className={styles.noMatch}>{`No ${nouns.other} match these choices.`}</p>
      <button type="button" className={styles.secondaryButton} onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}
